/**
 * Adapter that converts server-driven WizardState into the shape
 * that existing onboarding step components expect (StepConfig, etc.).
 *
 * Each server step has a `block_id` that maps to a UI `type` (the StepType),
 * plus optional config overrides that carry fields, instructions, labels, etc.
 */

import type {
  StepConfig,
  StepType,
  Achievement,
  OnboardingConfig,
} from "../data/onboardingConfig.types";
import type { WizardState, StepState } from "../services/naraDoor";

// -------------------------------------------------------
// block_id -> StepType mapping
// -------------------------------------------------------
const BLOCK_TYPE_MAP: Record<string, StepType> = {
  splash: "splash",
  splash_screen: "splash",
  form: "form",
  collect_name: "form",
  collect_company_name: "form",
  generic_form: "form",
  action_confirmation: "action_confirmation",
  create_workspace: "action_confirmation",
  send_sms_invite: "action_confirmation",
  instruction: "instruction",
  polling: "polling",
  check_documents: "polling",
  completion: "completion",
};

function resolveStepType(blockId: string, uiType?: string): StepType {
  // Prefer explicit ui_type from the server block, fall back to block_id prefix
  if (uiType && BLOCK_TYPE_MAP[uiType]) {
    return BLOCK_TYPE_MAP[uiType];
  }
  // Try matching block_id as-is or as a prefix (e.g. "form_profile" -> "form")
  if (BLOCK_TYPE_MAP[blockId]) {
    return BLOCK_TYPE_MAP[blockId];
  }
  for (const [prefix, type] of Object.entries(BLOCK_TYPE_MAP)) {
    if (blockId.startsWith(`${prefix}_`) || blockId.startsWith(prefix)) {
      return type;
    }
  }
  return "instruction"; // Safe default
}

// -------------------------------------------------------
// Convert a single StepState into StepConfig
// -------------------------------------------------------
function toStepConfig(step: StepState, index: number, uiConfig?: Record<string, any> | null): StepConfig {
  const config = step.data ?? {};
  const type = resolveStepType(step.block_id, config.ui_type);

  return {
    id: step.step_id,
    type,
    title: config.title ?? `wizard.step.${step.block_id}.title`,
    subtitle: config.subtitle,
    // Form step fields
    fields: config.fields,
    // Action step
    action: config.action,
    loadingText: config.loadingText,
    successText: config.successText,
    // Instruction step
    instructions: config.instructions,
    // Polling step
    pollAction: config.pollAction,
    pollIntervalMs: config.pollIntervalMs,
    skipLabel: config.skipLabel,
    waitingText: config.waitingText,
    // Completion step
    ctaLabel: config.ctaLabel,
    ctaAction: config.ctaAction,
    // Points & celebrations
    points: config.points ?? 0,
    achievementId: config.achievementId,
    celebrate: config.celebrate,
    // Navigation
    nextLabel: config.nextLabel,
    backAllowed: config.backAllowed ?? (index > 0),
    autoAdvance: config.autoAdvance,
  };
}

// -------------------------------------------------------
// Build achievements array from step configs
// -------------------------------------------------------
function buildAchievements(stepConfigs: StepConfig[], uiConfig?: Record<string, any> | null): Achievement[] {
  // Use achievements from ui_config if available (server-driven text)
  const serverAchievements = (uiConfig?.achievements as any[]) || [];
  const serverMap = new Map<string, any>();
  for (const a of serverAchievements) {
    if (a.id) serverMap.set(a.id, a);
  }

  const achievements: Achievement[] = [];
  for (const step of stepConfigs) {
    if (step.achievementId) {
      const server = serverMap.get(step.achievementId);
      achievements.push({
        id: step.achievementId,
        title: server?.title ?? `wizard.achievement.${step.achievementId}.title`,
        description: server?.description ?? `wizard.achievement.${step.achievementId}.description`,
        icon: server?.icon ?? "Trophy",
        points: step.points,
      });
    }
  }
  return achievements;
}

// -------------------------------------------------------
// Public adapter
// -------------------------------------------------------
export interface AdaptedWizardState {
  /** Steps in the shape step components expect */
  steps: StepConfig[];
  /** Current step index (from server) */
  currentStep: number;
  /** Accumulated form data from server collected_data */
  formData: Record<string, any>;
  /** Merged ui_config from the template */
  uiConfig: Record<string, any>;
  /** Which steps the server considers completed */
  completedSteps: boolean[];
  /** Full OnboardingConfig-compatible object for gamification hooks */
  config: OnboardingConfig;
}

export function adaptServerState(state: WizardState): AdaptedWizardState {
  const uiConfig = state.ui_config ?? {};

  const stepConfigs = state.steps.map((s, i) => toStepConfig(s, i, state.ui_config));

  const completedSteps = state.steps.map(
    (s) => s.status === "completed" || s.status === "skipped"
  );

  const achievements = buildAchievements(stepConfigs, uiConfig);
  const totalPoints = stepConfigs.reduce((sum, s) => sum + s.points, 0);

  const config: OnboardingConfig = {
    version: 1,
    steps: stepConfigs,
    achievements,
    totalPoints,
  };

  return {
    steps: stepConfigs,
    currentStep: state.current_step_index,
    formData: state.collected_data ?? {},
    uiConfig,
    completedSteps,
    config,
  };
}
